<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="profiles"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">

          <app-back-button v-bind:color="toolbarColorDark" />

          <!--<v-icon large :color="appColor_Abby" class="mr-2">library_books</v-icon>-->
          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>
          
          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog v-model="editDialog" max-width="350px" :persistent="true">
              <loading :active.sync="isSaving" :can-cancel="false" loader="dots" :color="appColor_Progress"></loading>
              <template v-slot:activator="{ on }">
                <v-btn slot="activator" :color="toolbarColorDark" small dark class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">{{ getExpression('Button', 'CreatePlaylist') }}</v-btn>
              </template>
              <v-toolbar dense dark flat :color="toolbarColorDark">
                <v-toolbar-title><v-icon class="pr-2">edit</v-icon> {{ formTitle }}</v-toolbar-title>
              </v-toolbar>
              <v-card>
                <v-card-text class="pt-3 pl-2 pr-2 pb-0 ma-0">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col cols="12" xs="12">
                          <v-text-field
                            v-model="editedProfile.name"
                            :label="getExpression('Label', 'Name')"
                            v-validate="'required|max:50'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            maxlength="50"
                            ref="name"
                          />
                        </v-col>
                        <v-col cols="12" xs="12">
                          <v-text-field
                            v-model="editedProfile.description"
                            :label="getExpression('Label', 'Description')"
                            maxlength="100"
                          />
                        </v-col>
                        <template v-if="editedProfile.id > 0">
                          <v-col cols="12" xs="6">
                            <v-switch
                              v-model="editedProfile.isActive"
                              :disabled="editedProfile.isDisableActivate"
                              :label="getExpression('Label', 'Activate')"
                              class="mt-1 pa-0"
                            />
                          </v-col>
                        </template>
                        <template v-if="!editedProfile.isValid && editedProfile.id > 0">
                          <v-col cols="12" xs="6">
                            <p class="text-sm-left font-weight-black">Profile contains no media</p>
                          </v-col>
                        </template>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions class="pt-0">
                  <v-spacer />
                  <v-btn @click="closeDialog" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="toolbarColorDark" dark @click="save" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-1">
          
          <!-- Kiosks Dropdown -->
          <v-col v-if="showKiosk" cols="12" sm="4">
            <kiosk-combobox
              v-model="kioskId"
              :options="kiosks"
              :label="getExpression('Label', 'Kiosk')"
              :placeholder="getExpression('Label', 'SelectKiosk')"
              :disabled="disableKiosk"
              class="mr-3"
            />
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" :sm="searchColWidth" >
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="disableSearch"
              clearable
              >
            </v-text-field>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.status="{ item }">
        <span class="text-center">
          <v-chip v-if="item.isValid !== null" dark small :color="item.isValid ? 'green' : 'red'">{{ getStatusText(item.isValid) }}</v-chip>
        </span>
      </template>

      <template v-slot:item.items="{ item }">
        <v-btn small dark @click="editMedia(item)" :color="appColor_AbbyDark" height="36" class="rounded-0">{{ getExpression('Button', 'EditContent') }}</v-btn>
      </template>

      <template v-slot:item.isActive="{ item }">
        <span class="mr-4">
          <v-icon v-if="item.isActive && item.isValid" :color="appColor_Selected">done</v-icon>
          <v-icon v-else-if="item.isActive && !item.isValid" :color="'grey lighten-2'">cancel</v-icon>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left pl-0 pr-0"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Profile Detail -->
    <v-dialog
      v-model="detailsDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition">
      <abby-profile-detail
        v-bind:id="currentProfileId"
        v-bind:sourceId="sourceId_Kiosk"
        v-bind:mediaPathTypes="mediaPathTypes"
        v-bind:profileDetails="profileDetails"
        v-bind:name="editedProfile.name"
        v-bind:ownerName="kioskName"
        v-bind:kioskId="kioskId"
        v-bind:defaultContainer="defaultContainer"
        v-bind:isActive="activeProfileId === editedProfile.id"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-on:cancel="detailCancel"
        v-on:done="detailDone"
        >
      </abby-profile-detail>
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import AbbyProfileDetail from '@/components/AbbyProfileDetail'
import { mapGetters } from 'vuex'
import { AppColor, AppStyle, Base64Image, Container, AbbyMediaSourceId, UserRole, MediaPathTypeId, ImageUri, KioskTypeId } from '@/config.js'
import KioskCombobox from '@/components/KioskCombobox'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'AbbyKioskProfileManager',
  components: {
    AbbyProfileDetail,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Abby: AppColor.KioskTypeAbbyLight,
      appColor_AbbyDark: AppColor.KioskTypeAbby,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // containers
      kioskPrefix: Container.KioskPrefix,
      publicContainer: Container.Public,
      
      // kiosk type ids
      kioskTypeId_Abby: KioskTypeId.Abby,

      // image uri
      base64Image_AudioIcon: Base64Image.AudioIcon,
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // source ids
      sourceId_Kiosk: AbbyMediaSourceId.Kiosk,

      // roles
      role_Manager: UserRole.Manager,

      // path types
      pathTypeId_AudioRadioShows: MediaPathTypeId.AudioRadioShows,

      isLoading: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      detailsDialog: false,
      kioskId: null,
      kiosks: [],
      profiles: [],
      profileDetails: [],
      mediaPathTypes: [],
      editedIndex: -1,
      currentProfileId: null,
      editedProfile: {
        id: -1,
        residentId: null,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        createDate: null,
        status: null,
        isValid: false
      },
      defaultProfile: {
        id: -1,
        residentId: null,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        createDate: null,
        status: null,
        isValid: false
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeKioskItem',
      'isAdmin',
      'isDomainManager',
      'isKioskManager',
      'isResidentManager',
      'previousRoute',
      'locale']),
    title () {
      return !this.isAdmin
        ? this.kioskName
        : 'Community Playlists'
    },
    formTitle () {
      return this.editedProfile.id < 0
        ? this.getExpression('FormTitle', 'NewPlaylist')
        : this.getExpression('FormTitle', 'EditPlaylist')
    },
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.kiosks.length === 0) {
        return this.getExpression('Alert', 'NoAbbyKiosks')
      }

      if (this.profiles.length === 0 && this.kioskId !== null) {
        return this.getExpression('Alert', 'NoPlaylists')
      }

      if (this.profiles.length === 0 && this.kioskId === null) {
        return this.getExpression('Alert', 'NoKioskSelected')
      }

      return ''
    },
    dictionary () {
      return {
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '50')
          }
        }
      }
    },
    toolbarColor () {
      return this.appColor_Abby
    },
    toolbarColorDark () {
      return this.appColor_AbbyDark
    },
    isFromKioskDashboard () {
      return this.previousRoute.indexOf('KioskDashboard') >= 0
        || (this.previousRoute.indexOf('Home') >= 0 && this.isKioskManager)
    },
    kioskName () {
      if (this.isFromKioskDashboard) return this.activeKioskItem.serialNumber
      
      const self = this
      const kiosks = self.kiosks.filter(value => {
        return value.id === self.kioskId
      })

      const kioskDesc = kiosks.length > 0
        ? kiosks[0].name
        : ''
        
      return kiosks.length > 0
        ? kioskDesc.substring(0, kioskDesc.indexOf(' '))
        : ''
    },
    kioskContainer () {
      if (this.isFromKioskDashboard) return this.activeKioskItem.container
      
      const self = this
      const kiosks = self.kiosks.filter(value => {
        return value.id === self.kioskId
      })

      return kiosks.length > 0
        ? kiosks[0].container
        : null
    },
    headers () {
      return [
        {
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: '20%'
        },
        {
          text: this.getExpression('Label', 'Description'),
          value: 'description',
          align: 'left',
          width: '25%'
        },
        {
          text: this.getExpression('Label', 'Media'),
          value: 'items',
          align: 'center',
          width: '15%',
          sortable: false
        },
        {
          text: this.getExpression('Label', 'Status'),
          value: 'status',
          align: 'center',
          width: '10%'
        },
        {
          text: this.getExpression('Label', 'Active'),
          value: 'isActive',
          align: 'center',
          width: '10%'
        },
        {
          text: this.getExpression('Label', 'Added'),
          value: 'createDate',
          align: 'left',
          width: '15%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ]
    },
    activeProfileId () {
      var profiles = this.profiles.filter(value => {
        return value.isActive
      })
      if (profiles.length > 0) {
        return profiles[0].id
      }
      return -1
    },
    defaultContainer () {
      if (this.kioskContainer !== null) {
        return this.kioskContainer
      }
      return this.publicContainer
    },
    disableAdd () {
      const kioskId = this.kioskId ?? 0
      return kioskId === 0
    },
    showKiosk () {
      return this.isAdmin && !this.isFromKioskDashboard
    },
    disableKiosk () {
      return this.kiosks.length === 0 || this.isLoading
    },
    disableSearch () {
      const kioskId = this.kioskId ?? 0
      return this.isLoading || kioskId === 0
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKiosk
        ? '8'
        : '12'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this
      self.isLoading = true

      self.loadMediaPathTypes().then(() => {

        // dashboard mode
        if (self.isFromKioskDashboard) {
          self.kioskId = self.activeKioskItem.id

        // admin mode
        } else {
          appService.getKioskComboboxOptions(self.kioskTypeId_Abby).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.kiosks = data.options
              self.kioskId = 0
            }
            self.isLoading = false
          })
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadMediaPathTypes () {
      const self = this
      return new Promise(resolve => {
        const isSystem = true
        const isSharable = true

        appService.getAbbyMediaPathTypes(isSystem, isSharable, self.profile.languageId).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.mediaPathTypes = data.mediaPathTypes
            const excludeTypes = [self.pathTypeId_AudioRadioShows]

            self.mediaPathTypes = data.mediaPathTypes
              .filter(pt => !excludeTypes.includes(pt.id))
            
            resolve()
          }
        })
      })
    },

    loadProfiles () {
      const self = this
      self.isLoading = true
      appService.getAbbyKioskProfiles(self.kioskId, self.locale).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.profiles = data.profiles
          self.loadStatuses()
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    loadStatuses () {
      const self = this
      appService.getAbbyKioskProfileStatuses(self.kioskId).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          const statuses = data.profiles
          statuses.forEach(ps => {
            self.setStatus(ps.id, ps.hasRequiredMedia)
          })
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    edit (item) {
      this.editedIndex = this.profiles.findIndex(p => p.id === item.id)
      this.editedProfile = Object.assign({}, item)

      this.editedProfile.isDisableActivate = this.editedProfile.id === this.activeProfileId
        || !this.editedProfile.isValid
      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.profiles.findIndex(p => p.id === item.id)
      const name = self.profiles[index].name

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeletePlaylist'),
        text: `<b>${name}</b>`,
        onConfirm: () => {
          self.isDeleting = true

          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteAbbyProfile(item.id, false, updateByUserId).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.profiles.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          self.isSaving = true
          if (isEditMode) {
            var editedProfile = self.profiles[self.editedIndex]
            Object.assign(editedProfile, self.editedProfile)
            const profileModel = self.getModel(editedProfile, isEditMode)
            appService.updateAbbyProfile(editedProfile.id, profileModel, false).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                self.isSaving = false
                if (editedProfile.isActive) {
                  self.setActive(editedProfile.id)
                }
              }
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            appService.addAbbyProfile(self.getModel(self.editedProfile, false)).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedProfile.id = data.profile.id
                self.editedProfile.kioskId = data.profile.kioskId
                self.editedProfile.isActive = data.profile.isActive
                self.editedProfile.createDate = data.profile.createDate
                self.editedProfile.status = 'Missing required media'
                self.editedProfile.isValid = false
                self.profiles.unshift(self.editedProfile)
                self.profiles[0].profileDetails = []
              }
              self.isSaving = false
              self.closeDialog()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (p, isEditMode) {
      if (isEditMode) {
        return {
          kioskId: this.kioskId,
          name: p.name,
          description: p.description,
          isActive: p.isActive,
          updateByUserId: this.profile.id,
          createDate: p.createDate
        }
      } else {
        return {
          kioskId: this.kioskId,
          name: p.name,
          description: p.description,
          updateByUserId: this.profile.id
        }
      }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedProfile()
      }, 300)
    },

    resetEditedProfile () {
      this.editedProfile = Object.assign({}, this.defaultProfile)
      this.editedIndex = -1
      this.$validator.reset()
    },

    editMedia (item) {
      this.editedIndex = this.profiles.indexOf(item)
      this.editedProfile = Object.assign({}, item)
      this.currentProfileId = this.editedProfile.id
      this.cloneProfileDetails()
      this.detailsDialog = true
    },

    cloneProfileDetails () {
      const self = this

      self.profileDetails = []
      const profiles = self.profiles.filter(value => {
        return value.id === self.editedProfile.id
      })

      if (profiles.length > 0) {
        const profile = profiles[0]
        profile.profileDetails.forEach(pd => {
          const model = self.getDetailModel(pd)
          self.profileDetails.push(model)
        })
      }
    },

    setStatus (id, isValid) {
      const self = this
      const profiles = self.profiles.filter(p => p.id === id)
      const profile = profiles.length > 0
        ? profiles[0]
        : null

      if (profile !== null) {
        profile.isValid = isValid
      }
    },

    getStatus (id) {
      const self = this
      appService.getAbbyKioskProfileStatus(id).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          const isValid = data.profile.hasRequiredMedia
          self.setStatus(id, isValid)
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    detailDone ({ profileDetails }) {
      const self = this

      const p = self.profiles.filter(value => {
        return value.id === self.editedProfile.id
      })[0]

      p.profileDetails = []
      profileDetails.forEach(pd => {
        const model = self.getDetailModel(pd)
        p.profileDetails.push(model)
      })

      self.getStatus(p.id)
      self.detailsDialog = false
      self.resetEditedProfile()
    },

    getDetailModel (pd) {
      return {
        id: pd.id,
        profileId: pd.profileId,
        mediaFileId: pd.mediaFileId,
        addDate: pd.addDate,
        containerId: pd.containerId,
        container: pd.container,
        containerOwner: pd.containerOwner,
        isLinked: pd.isLinked,
        filename: pd.filename,
        title: pd.title,
        mediaPathTypeId: pd.mediaPathTypeId,
        mediaPath: pd.mediaPath,
        category: pd.category,
        responseTypeId: pd.responseTypeId,
        createDate: pd.createDate,
        contentType: pd.contentType,
        size: pd.size,
        duration: pd.duration,
        thumbnail: pd.thumbnail
      }
    },

    detailCancel () {
      this.detailsDialog = false
      this.resetEditedProfile()
    },

    setActive (id) {
      this.profiles.forEach(p => {
        p.isActive = p.id === id
      })
    },

    getStatusText (isValid) {
      return isValid
        ? this.getExpression('Label', 'Ready')
        : this.getExpression('Label', 'MissingMedia')
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.name.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        item.description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    kioskId () {
      if (this.kioskId > 0) {
        this.loadProfiles()
      } else this.profiles = []
    },

    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to name input in the edit dialog
          self.$refs.name.focus()
        }, 300)
      }
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>
